import React, { useContext, useState } from "react";
import { FormData, FormErrors } from "../../types/confirmation";
import { visaRequiredCountries } from "../../util/visaRequiredCountries";
import axios from "axios";
import { base_url } from "../../config";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Loader2,
} from "lucide-react";
import RegistrationContext from "../../contexts/RegistrationContext";

export const VisaLetterApplication: React.FC = () => {
  const { data, setData, setError } = useContext(RegistrationContext);
  function ucfirst(str: string | null): string | null {
    if (!str) return str; // Handle empty or null strings
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function CompactVisaLetterEmpty({ id = "1" }: { id?: string }) {
    const [formData, setFormData] = useState<FormData>({
      title: "",
      surname: "",
      givenName: "",
      gender: "",
      nationality: "",
      birthDate: "",
      placeOfBirth: "",
      passport: "",
      passportIssueDate: "",
      passportExpiryDate: "",
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {};
      const required: Array<keyof FormData> = [
        "title",
        "surname",
        "givenName",
        "gender",
        "nationality",
        "birthDate",
        "placeOfBirth",
        "passport",
        "passportIssueDate",
        "passportExpiryDate",
      ];
      for (const k of required) {
        if (!String(formData[k] ?? "").trim()) newErrors[k] = "Required";
      }
      if (
        formData.passport.trim() &&
        !/^[A-Z0-9]{6,12}$/i.test(formData.passport)
      ) {
        newErrors.passport = "Invalid format";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleChange = (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (validateForm()) {
        setIsSubmitting(true);
        try {
          var requestBody: any = {
            type: id,
            title: formData.title,
            surname: formData.surname,
            given_name: formData.givenName,
            gender: formData.gender,
            nationality: formData.nationality,
            birth_date: formData.birthDate,
            place_of_birth: formData.placeOfBirth,
            passport_number: formData.passport,
            passport_issue_date: formData.passportIssueDate,
            passport_expiry_date: formData.passportExpiryDate,
          };
          const response = await axios.post(
            `${base_url}/api/visa_letter`,
            requestBody
          );
          if (response.status === 200) {
            setData({ ...data, visa_letters: response.data.visa_letter });
          }
        } catch (error: any) {
          setIsSubmitting(false);
        }
      }
    };

    const fieldClass = (key: keyof FormErrors) =>
      `mt-1 block w-full rounded-md border ${
        errors[key] ? "border-red-300" : "border-gray-300"
      } px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500`;

    return (
      <div
        className="bg-white rounded-lg shadow border-l-4 border-orange-500 mb-3"
        key={id}
      >
        <div
          className="flex items-center justify-between p-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <FileText className="h-4 w-4 text-orange-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              Visa Letter Application for {ucfirst(id)}
            </h3>
          </div>
          <div className="flex items-center">
            <span className="text-xs font-medium text-orange-500 mr-2">
              Missing Application
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-3 border-t border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <label
                    htmlFor={`title-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Title*
                  </label>
                  <select
                    id={`title-${id}`}
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={fieldClass("title")}
                  >
                    <option value="">Select…</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                    <option value="Mx">Mx</option>
                  </select>
                  {errors.title && (
                    <p className="mt-0.5 text-xs text-red-600">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`surname-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Surname*
                  </label>
                  <input
                    type="text"
                    id={`surname-${id}`}
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className={fieldClass("surname")}
                  />
                  {errors.surname && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.surname}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`givenName-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Given Name*
                  </label>
                  <input
                    type="text"
                    id={`givenName-${id}`}
                    name="givenName"
                    value={formData.givenName}
                    onChange={handleChange}
                    className={fieldClass("givenName")}
                  />
                  {errors.givenName && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.givenName}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`gender-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Sex*
                  </label>
                  <select
                    id={`gender-${id}`}
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={fieldClass("gender")}
                  >
                    <option value="">Select…</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.gender}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`nationality-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Nationality*
                  </label>
                  <select
                    id={`nationality-${id}`}
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className={fieldClass("nationality")}
                  >
                    <option value="">Select…</option>
                    {visaRequiredCountries.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-gray-500 italic">
                    Only nationalities that require a Schengen visa to enter
                    Germany are listed. If your country is not shown, you do
                    not need a visa for short stays.
                  </p>
                  {errors.nationality && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.nationality}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`birthDate-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Date of birth*
                  </label>
                  <input
                    type="date"
                    id={`birthDate-${id}`}
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    className={fieldClass("birthDate")}
                  />
                  {errors.birthDate && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.birthDate}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`placeOfBirth-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Place of birth*
                  </label>
                  <input
                    type="text"
                    id={`placeOfBirth-${id}`}
                    name="placeOfBirth"
                    value={formData.placeOfBirth}
                    onChange={handleChange}
                    className={fieldClass("placeOfBirth")}
                  />
                  {errors.placeOfBirth && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.placeOfBirth}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`passport-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Passport No.*
                  </label>
                  <input
                    type="text"
                    id={`passport-${id}`}
                    name="passport"
                    value={formData.passport}
                    onChange={handleChange}
                    className={fieldClass("passport")}
                  />
                  {errors.passport && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.passport}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`passportIssueDate-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Date of issue*
                  </label>
                  <input
                    type="date"
                    id={`passportIssueDate-${id}`}
                    name="passportIssueDate"
                    value={formData.passportIssueDate}
                    onChange={handleChange}
                    className={fieldClass("passportIssueDate")}
                  />
                  {errors.passportIssueDate && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.passportIssueDate}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`passportExpiryDate-${id}`}
                    className="block text-xs font-medium text-gray-700"
                  >
                    Date of expiry*
                  </label>
                  <input
                    type="date"
                    id={`passportExpiryDate-${id}`}
                    name="passportExpiryDate"
                    value={formData.passportExpiryDate}
                    onChange={handleChange}
                    className={fieldClass("passportExpiryDate")}
                  />
                  {errors.passportExpiryDate && (
                    <p className="mt-0.5 text-xs text-red-600">
                      {errors.passportExpiryDate}
                    </p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-1.5 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin h-3 w-3 mr-1" />
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  function CompactVisaLetterProcessing({ id = "1" }: { id: string }) {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
      <div
        className="bg-white rounded-lg shadow border-l-4 border-blue-500 mb-3"
        key={id}
      >
        <div
          className="flex items-center justify-between p-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-blue-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              Visa Letter Application for {ucfirst(id)}
            </h3>
          </div>
          <div className="flex items-center">
            <Loader2 className="h-3 w-3 text-blue-500 animate-spin mr-1" />
            <span className="text-xs font-medium text-blue-500 mr-2">
              Processing
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-3 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-2">
              Your visa letter application is currently being processed. This
              typically takes 2-3 business days.
            </p>

            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
              <div className="bg-blue-500 h-1.5 rounded-full w-1/2"></div>
            </div>

            <p className="text-xs text-gray-500">
              Estimated completion: 48 hours
            </p>
          </div>
        )}
      </div>
    );
  }

  function CompactVisaLetterDeclined({
    id = "1",
    r = "",
  }: {
    id: string;
    r: string;
  }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isReapplying, setIsReapplying] = useState(false);

    const handleReapply = (e: React.MouseEvent) => {
      e.stopPropagation();

      const reapplyHandler = async () => {
        setIsReapplying(true);
        try {
          const response = await axios.post(`${base_url}/api/reapply_visa`, {
            type: id,
          });
          if (response.status === 200) {
            setData({ ...data, visa_letters: response.data.visa_letters });
            // Handle success (e.g., show a success message, redirect, etc.)
          }
        } catch (error: any) {
          setError(
            "Unexpected error. " + error.response?.data?.message ||
              error.message
          );
        } finally {
          setIsReapplying(false);
        }
      };
      reapplyHandler();
    };

    return (
      <div
        className="bg-white rounded-lg shadow border-l-4 border-red-500 mb-3"
        key={id}
      >
        <div
          className="flex items-center justify-between p-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              Visa Letter Application for {ucfirst(id)}
            </h3>
          </div>
          <div className="flex items-center">
            <span className="text-xs font-medium text-red-500 mr-2">
              Declined
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-3 border-t border-gray-100">
            <div className="bg-red-50 p-2 rounded-md mb-2">
              <p className="text-xs text-red-800">
                Your visa letter application has been declined.
              </p>
            </div>

            <div className="mb-3">
              <h4 className="text-xs font-medium text-gray-700 mb-1">
                Reason:
              </h4>
              <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-200">
                {r}
              </p>
            </div>

            <button
              onClick={handleReapply}
              disabled={isReapplying}
              className="w-full flex justify-center py-1.5 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {isReapplying ? (
                <>
                  <Loader2 className="animate-spin h-3 w-3 mr-1" />
                  Processing...
                </>
              ) : (
                "Reapply"
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {data.visa_letters.map((x: any) => {
        switch (x.status) {
          case "pending":
            return <CompactVisaLetterProcessing key={x.type} id={x.type} />;
          case "ko":
            return (
              <CompactVisaLetterDeclined
                key={x.type}
                id={x.type}
                r={x.rejection_reason}
              />
            );
          case "empty":
            return <CompactVisaLetterEmpty key={x.type} id={x.type} />;
          default:
            return null;
        }
      })}
    </>
  );
};
